import type { SecondOrderDomainKey } from '@akem/shared';
import { Box, useTheme } from '@mui/material';
import React from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { WidgetHost } from '../../dashboard/WidgetHost.js';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface IntelligenceTile {
  id: SecondOrderDomainKey;
  title: string;
  profileIds: string[];
  profileId?: string;
  viewMode: 'all' | 'portfolio';
}

interface IntelligenceGridProps {
  tiles: IntelligenceTile[];
}

export const IntelligenceGrid: React.FC<IntelligenceGridProps> = ({ tiles }) => {
  const theme = useTheme();

  // Default layout: 4 columns on large screens, 2 on medium, 1 on small
  // Each item is 1 unit wide (relative to columns) and 4 units tall (arbitrary height unit)
  const generateLayout = (cols: number) => {
    return tiles.map((tile, index) => ({
      i: tile.id,
      x: index % cols,
      y: Math.floor(index / cols) * 4,
      w: 1,
      h: 4,
      minW: 1,
      minH: 2,
    }));
  };

  const layouts = React.useMemo(() => ({
    lg: generateLayout(4),
    md: generateLayout(2),
    sm: generateLayout(1),
  }), [tiles]);

  return (
    <Box sx={{ 
      '& .react-grid-item': { transition: 'none' }, // Disable transition during drag for performance
      '& .react-grid-placeholder': { backgroundColor: 'rgba(184, 137, 255, 0.1) !important', borderRadius: '10px' }
    }}>
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 4, md: 2, sm: 1, xs: 1, xxs: 1 }}
        rowHeight={100} // Base height for grid rows
        margin={[24, 24]} // Gaps between items
        isDraggable
        isResizable
        draggableHandle=".drag-handle" // Optional: restrict dragging to a handle if needed, or remove to drag anywhere
      >
        {tiles.map((tile) => (
          <Box
            key={tile.id}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: '#14121a',
              borderRadius: '10px',
              border: '1px solid rgba(255,255,255,0.05)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.35)',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <WidgetHost
              type="akemIntelligence"
              config={{
                domain: tile.id,
                title: tile.title,
                profileIds: tile.profileIds,
                profileId: tile.profileId,
                maxArtifacts: 4,
                viewMode: tile.viewMode,
              }}
              disableCard
              contentSx={{ height: '100%' }}
            />
            {/* Resize Handle Styling (optional custom override, or rely on default) */}
            <Box
              className="react-resizable-handle"
              sx={{
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: 3,
                  bottom: 3,
                  width: 5,
                  height: 5,
                  borderRight: '2px solid rgba(255,255,255,0.3)',
                  borderBottom: '2px solid rgba(255,255,255,0.3)',
                }
              }}
            />
          </Box>
        ))}
      </ResponsiveGridLayout>
    </Box>
  );
};
